import styles from "./BoardIllustration.module.css";

/**
 * The cold-start board: ten positions drawn, no deals in them (`WEB-023`, canvas
 * `contracts/design/web-homepage/Dealbid Cold Start.dc.html` artboards 6–9).
 *
 * An empty board used to be a sentence. A founder — the only visitor who can fill it — was
 * told what Dealbid is instead of shown it, so this draws the board they would be joining:
 * ten rows, tiered exactly as the real board is tiered, with the invitation over the top.
 *
 * **Why it is line-work and not a blur.** This is the whole difficulty of the state, and
 * artboard 10 is the argument. An obscured row is the web's convention for content that
 * exists behind a wall; a reader who reads these that way has been told an empty marketplace
 * has inventory, which is `AGENTS.md` § 11's fake scarcity arriving as artwork rather than as
 * a number. So nothing here is blurred, hatched or half-hidden. The rows are unfilled dashed
 * outlines with sharp hairline rules where text would sit — no letterforms to squint at — and
 * carry no name, vendor, price, discount, badge or count, real or invented. The only glyphs
 * are the rank numerals, and those are true: the ten positions exist, their contents do not.
 *
 * **Why it is not the loading skeleton.** `docs/TESTING.md` wants loading, empty, error and
 * data distinct, and `Skeleton` already owns "something is arriving" — solid grey pill fills,
 * shimmering. This is the opposite construction: unfilled outlines, hairline rules, visible
 * brass. Fills against line-work, told apart at a glance on a slow connection in either
 * theme. It is also static, so `prefers-reduced-motion` has nothing here to turn off.
 *
 * **Tiering is the point, not decoration.** Position 1 keeps the larger numeral and the
 * accent ring so a founder can see a position is worth more than the one below it before a
 * single deal exists. The ring is dashed and unfilled where the real row uses `--accent-soft`
 * — a fill reads as content, and there is no content.
 *
 * The rows are `aria-hidden`: a screen-reader user gets the caption and the invitation, never
 * a phantom board. The caption itself is not hidden. It is the sentence that says what this
 * is, and it is the last thing that should be available only to people who can see.
 *
 * **It draws the positions the board has not filled, not always all ten** (`WEB-040`). The
 * board a founder meets during cold start is usually neither empty nor full: canon § 6 seats
 * every approved deal and `D-017` seats it the moment it is approved, so one, three or nine
 * real rows above a drawing of what is left is the ordinary state, not an edge case. `from`
 * is the first rank still to be filled, so the tiering below stays a property of the **rank**
 * — draw from 2 and the first drawn row is rank 2's stepped treatment, not rank 1's lead —
 * and the drawing continues the board rather than restarting it.
 */

/** The ten positions. Tier follows the built board: 1, then 2–3, then 4–10. */
const POSITIONS = Array.from({ length: 10 }, (_, index) => {
  const rank = index + 1;
  return {
    rank: rank < 10 ? `0${String(rank)}` : String(rank),
    tier: rank === 1 ? styles.lead : rank <= 3 ? styles.stepped : styles.compact,
    /*
     * Varied rule widths, fixed per position rather than random. A column of identical bars
     * reads as a pattern; deals are not identical lengths. Deterministic because a
     * placeholder that reshuffles on every render is motion nobody asked for.
     */
    name: ["72%", "60%", "64%", "56%", "60%", "54%", "62%", "52%", "58%", "60%"][index],
    description: ["46%", "40%", "44%", "36%", "42%", "38%", "45%", "34%", "40%", "43%"][index],
  };
});

export function BoardIllustration({ from = 1 }: { from?: number }) {
  const drawn = POSITIONS.slice(Math.max(0, from - 1));

  return (
    <div className={styles.illustration}>
      <div className={styles.rows} aria-hidden="true">
        {drawn.map((position) => (
          <div key={position.rank} className={`${styles.row} ${position.tier}`}>
            <span className={`${styles.rank} figure`}>{position.rank}</span>
            <span className={styles.lines}>
              <span className={styles.nameRule} style={{ inlineSize: position.name }} />
              <span
                className={styles.descriptionRule}
                style={{ inlineSize: position.description }}
              />
            </span>
            <span className={styles.prices}>
              <span className={styles.priceRule} />
              <span className={styles.originalRule} />
            </span>
            {/* Where the row's action sits. An outline, because there is nothing to open. */}
            <span className={styles.actionSlot} />
          </div>
        ))}
      </div>
      {/*
        The caption is the sentence a screen-reader user gets *instead of* the rows, so it
        has to be true of what was actually drawn. On an empty board that is all ten
        positions; on a partly filled one it is the remainder, and saying "ten positions, no
        deals" over a board that visibly has three would be the one thing this drawing must
        never do — describe the marketplace as emptier than it is.

        Neither wording counts places down (`AGENTS.md` § 11): the drawing shows how much of
        the board is unfilled, and no sentence anywhere puts a number on what is left.
      */}
      <p className={`${styles.caption} figure`}>
        {from === 1
          ? "Illustration — ten positions, no deals on the board yet."
          : "Illustration — the board’s remaining positions, drawn. No deal is hidden behind them."}
      </p>
    </div>
  );
}
